import { Link as RouterLink } from "react-router-dom";
import { Alert, AlertIcon, Box, Heading, HStack, Link, SimpleGrid, Stat, StatHelpText, StatLabel, StatNumber, Text, VStack } from "@chakra-ui/react";
import PlacingStatusBadge from "./PlacingStatusBadge";
import PartyName from "./PartyName";
import { RoleBadge, StandsBadge } from "./LineBadges";
import { formatDate, readParticipation } from "./placingUtils";

const pct = (n) => (n == null ? "—" : `${Math.round(Number(n) * 10000) / 10000}%`);

/** Carrier: a signed line advice, the carrier's confirmed line (read-only; no acknowledge yet). */
const CarrierSignedLineAdviceView = ({ advice }) => {
  const p = readParticipation(advice);
  const difference = p.signedShare != null && p.writtenShare != null ? Math.round((p.signedShare - p.writtenShare) * 10000) / 10000 : null;
  const signedAll = Number(advice.signingPercentage) === 100;

  return (
    <VStack align="stretch" spacing={5}>
      <Alert status="success" variant="left-accent" borderRadius="md">
        <AlertIcon />
        <Text>
          Your line on <b>{advice.name || "this risk"}</b> has been signed at <b>{pct(p.signedShare)}</b>.
        </Text>
      </Alert>

      <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
        <HStack justify="space-between" mb={3}>
          <HStack spacing={3}>
            <Heading size="md">Signed line advice</Heading>
            <RoleBadge role={advice.receiver?.roleCode} fontSize="md" px={3} />
          </HStack>
          <PlacingStatusBadge status="signed" fontSize="md" variant="solid" />
        </HStack>
        <SimpleGrid columns={[1, 3]} spacing={4} mb={4}>
          <Stat>
            <StatLabel>Written line</StatLabel>
            <StatNumber>{pct(p.writtenShare)}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Signed line</StatLabel>
            <StatNumber color="green.600">{pct(p.signedShare)}</StatNumber>
            {difference != null && <StatHelpText>{difference === 0 ? "no change" : `${difference > 0 ? "+" : "−"}${Math.abs(difference)} from written`}</StatHelpText>}
          </Stat>
          <Stat>
            <StatLabel>Signing down</StatLabel>
            <StatNumber fontSize="lg">{signedAll ? "None needed" : pct(advice.signingPercentage)}</StatNumber>
            {!signedAll && advice.signingPercentage != null && (
              <StatHelpText>Lines not standing were signed at {pct(advice.signingPercentage)} of written</StatHelpText>
            )}
          </Stat>
        </SimpleGrid>
        {advice.acceptedConditions && (
          <Alert status="info" fontSize="sm" mb={3} borderRadius="md">
            <AlertIcon />
            <Text whiteSpace="pre-wrap">Signed with your conditions accepted: <i>{advice.acceptedConditions}</i></Text>
          </Alert>
        )}
        {p.lineToStandIndicator && (
          <HStack mb={3}>
            <StandsBadge />
            <Text fontSize="sm">Your line stood, so it wasn't signed down</Text>
          </HStack>
        )}
        <SimpleGrid columns={[1, 2, 4]} spacing={3} fontSize="sm">
          <Box><Text fontSize="xs" color="gray.500">Broker</Text><PartyName partyId={advice.sender?.partyId} /></Box>
          <Box><Text fontSize="xs" color="gray.500">As</Text><PartyName partyId={advice.receiver?.partyId} /></Box>
          <Box><Text fontSize="xs" color="gray.500">Sent</Text>{formatDate(advice.receiver?.sentDate || advice.createdDate) || "—"}</Box>
          {advice.documentId && (
            <Box>
              <Text fontSize="xs" color="gray.500">Contract document</Text>
              <Link as={RouterLink} color="blue.600" to={`/editor?doc=${advice.documentId}`}>Open document</Link>
            </Box>
          )}
        </SimpleGrid>
        {advice.notes && (
          <Box mt={3}>
            <Text fontSize="xs" color="gray.500">Notes from the broker</Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">{advice.notes}</Text>
          </Box>
        )}
        {advice.lineRequestId && (
          <Text fontSize="sm" mt={3}>
            {/* The offer is listed on its line request */}
            Signed from your line on{" "}
            <Link as={RouterLink} color="blue.600" to={`/placing/linerequests/${advice.lineRequestId}`}>this line request</Link>
            {advice.offerId && <Text as="span" color="gray.500"> (the line you wrote is listed there)</Text>}
          </Text>
        )}
      </Box>
    </VStack>
  );
};

export default CarrierSignedLineAdviceView;
